import React, { useMemo } from 'react'
import * as R from 'ramda'
import { linearGradient, transparentize } from 'polished'
import {
  useStoryblokEditable,
  StoryblokComponent
} from '@sb-konzept/gatsby-source-storyblok'
import Image from 'gatsby-image'
import Box from 'components/Box'
import { useTheme } from '@material-ui/styles'

const gradientPresets = {
  0: R.applySpec({
    colorStops: R.o(
      gradient => [
        `${gradient.light} 0%`,
        `${gradient.main} 50%`,
        `${gradient.dark} 100%`
      ],
      R.path(['theme', 'palette', 'gradient'])
    ),
    fallback: R.path(['theme', 'palette', 'gradient', 'main']),
    toDirection: R.path(['options', 'gradient'])
  })
}

const gradientTextPresets = {
  0: theme => theme.palette.gradient.contrastText
}

function getTextColor (theme, palette) {
  if (palette.gradientPreset && palette.autoTextColor) {
    return gradientTextPresets[palette.gradientPreset]?.(theme) || 'inherit'
  }
  if (palette.color && palette.autoTextColor) {
    const color = theme.palette[palette.color]?.[palette.variant]
    const contrastText = theme.palette[palette.color]?.contrastText
    return contrastText
      ? `${palette.color}.contrastText`
      : theme.palette.getContrastText(color || theme.palette.common.white)
  }
  return palette.textColor
    ? `${palette.textColor}.${palette.textVariant}`
    : 'inherit'
}

function getGradientPreset (theme, palette) {
  const gradient = gradientPresets[palette.gradientPreset]?.({
    theme,
    options: palette
  })
  return linearGradient(gradient).backgroundImage
}

function getBackgroundColor (theme, palette) {
  if (palette.gradient && palette.gradientPreset) {
    return getGradientPreset(theme, palette)
  }
  if (!palette.color) return null
  if (!palette.gradient && palette.opacity >= 1) return `${palette.color}.${palette.variant}`
  const color = theme.palette[palette.color][palette.variant]
  const alpha = transparentize(1 - palette.opacity, color)
  if (!palette.gradient) return alpha
  const alphaBegin = palette.gradientIntensity - 0.25
  const alphaEnd = palette.gradientIntensity + 0.25
  return `linear-gradient(
    ${palette.gradient},
    ${alpha},
    ${alpha} ${alphaBegin * 100}%,
    rgba(0,0,0,0) ${alphaEnd * 100}%,
    rgba(0,0,0,0)
  )`
}

function parseNumber (v) {
  const n = Number(v)
  if (Number.isNaN(n)) return v
  return n
}

function parseSpacingString (s) {
  if (!s || !s.trim()) return []
  return (
    s
      .trim()
      .split(/\s+/)
      .map(parseNumber) || []
  )
}

const lenEq = R.propEq('length')
function createSpacing (spaces) {
  return R.cond([
    [lenEq(1), s => [s[0], s[0], s[0], s[0]]],
    [lenEq(2), s => [s[0], s[1], s[0], s[1]]],
    [lenEq(3), s => [s[0], s[1], s[2], s[1]]],
    [lenEq(4), s => [s[0], s[1], s[2], s[3]]],
    [R.T, R.always([])]
  ])(spaces)
}

const directions = ['t', 'r', 'b', 'l']
const spacingMap = { margin: 'm', padding: 'p' }
const getSpacing = R.map(s =>
  R.compose(
    R.mergeAll,
    R.values,
    R.map(R.mergeAll),
    R.mapObjIndexed((v, k) =>
      R.addIndex(R.map)((vv, i) => ({ [k + directions[i]]: vv }), v)
    ),
    R.map(createSpacing),
    R.map(parseSpacingString),
    R.reduce((acc, k) => ({ ...acc, [spacingMap[k]]: s[k] }), {}),
    R.reject(R.equals('_uid')),
    R.keys
  )(s)
)

const mergeProps = R.compose(
  R.reduce(R.mergeDeepRight, {}),
  R.reject(R.not)
)

function StoryblokBox (props) {
  const { content } = props
  const { editableProps } = useStoryblokEditable()
  const theme = useTheme()

  const {
    body,
    backgroundImage,
    palette,
    maxWidths,
    textAlign,
    css,
    spacing: { plugin, _uid, ...spacing },
    disableInnerWrapper
  } = content

  const bgcolor = useMemo(() => getBackgroundColor(theme, palette), [
    palette,
    theme
  ])

  const computedProps = useMemo(
    () =>
      mergeProps([
        { position: 'relative' },
        { color: getTextColor(theme, palette) },
        textAlign && { textAlign },
        spacing && getSpacing(spacing),
        maxWidths && { container: true }
      ]),
    [maxWidths, palette, spacing, textAlign, theme]
  )

  return (
    <Box css={css} {...computedProps} {...editableProps}>
      <Box.Background>
        {backgroundImage.localFile
          && (backgroundImage.localFile.childImageSharp ? (
            <Image
              style={{ position: false }}
              fluid={backgroundImage.localFile.childImageSharp.fluid}
            />
          ) : (
            <img src={backgroundImage.localFile.publicURL} alt="" />
          ))}
        {bgcolor && <Box bgcolor={bgcolor} />}
      </Box.Background>
      {disableInnerWrapper ? (
        body?.map(b => <StoryblokComponent key={b.id || b._uid} content={b} />)
      ) : (
        <Box position="relative">
          {body?.map(b => (
            <StoryblokComponent key={b.id || b._uid} content={b} />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default StoryblokBox
